import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { PurchaseCreditsModal } from "@/components/dashboard/PurchaseCreditsModal";

interface CreditsContextType {
  credits: number;
  loading: boolean;
  refreshCredits: () => Promise<void>;
  openPurchaseModal: () => void;
}

const CreditsContext = createContext<CreditsContextType | undefined>(undefined);

export const useCredits = () => {
  const context = useContext(CreditsContext);
  if (!context) {
    throw new Error("useCredits must be used within a CreditsProvider");
  }
  return context;
};

interface CreditsProviderProps {
  children: ReactNode;
  userId: string;
}

export const CreditsProvider = ({ children, userId }: CreditsProviderProps) => {
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);

  const fetchCredits = async () => {
    if (!userId) return;
    
    const { data, error } = await supabase
      .from("user_credits")
      .select("balance")
      .eq("user_id", userId)
      .maybeSingle();

    if (data) {
      setCredits(data.balance);
    } else if (!error) {
      // Create credits record if doesn't exist
      await supabase.from("user_credits").insert({ user_id: userId, balance: 0 });
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCredits();
  }, [userId]);

  const refreshCredits = async () => {
    await fetchCredits();
  };

  const openPurchaseModal = () => {
    setShowPurchaseModal(true);
  };

  return (
    <CreditsContext.Provider value={{ credits, loading, refreshCredits, openPurchaseModal }}>
      {children}
      <PurchaseCreditsModal 
        open={showPurchaseModal} 
        onOpenChange={setShowPurchaseModal}
        userId={userId}
        onSuccess={refreshCredits}
      />
    </CreditsContext.Provider>
  );
};
